import React, { useState, useMemo } from 'react';
import { initials, formatMontant, uid } from '../utils/helpers';

const EMPTY = { id:'', nom:'', adresse:'', telephone:'', email:'', delegue:'', notes:'' };

export default function FicheEtablissement({ adherents, etablissements, cotisations, selectedYear, saveEtablissement, deleteEtablissement, selectedId, setSelectedId, goToAdherent }) {
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState(false);
  const [expandedEtabId, setExpandedEtabId] = useState(null);
  const yearCots = cotisations[selectedYear] || {};
  const selected = etablissements.find(e => e.id === selectedId);

  const stats = useMemo(() => {
    return etablissements.map(etab => {
      const actifs = adherents.filter(a => a.etablissementId === etab.id && a.statut === 'Actif');
      let collected = 0, expected = 0;
      actifs.forEach(adh => {
        Object.values(yearCots[adh.id] || {}).forEach(c => { if (c.status === 'paye') collected += (adh.montant||0); });
        const mult = adh.periodicite==='Trimestrielle'?4:adh.periodicite==='Semestrielle'?2:adh.periodicite==='Annuelle'?1:12;
        expected += (adh.montant||0)*mult;
      });
      return { id: etab.id, actifs: actifs.length, total: adherents.filter(a=>a.etablissementId===etab.id).length, collected, expected, taux: expected>0?Math.round(collected/expected*100):0 };
    });
  }, [etablissements, adherents, yearCots]);

  const f = k => v => setForm(p => ({...p, [k]: v}));

  const startCreate = () => { setForm(EMPTY); setEditing(true); setCreating(true); setSelectedId(null); };
  const startEdit = (e) => { setForm({...EMPTY,...e}); setEditing(true); setCreating(false); };
  const cancel = () => { setEditing(false); setCreating(false); };
  const save = () => {
    if (!form.nom.trim()) return;
    saveEtablissement({ ...form, id: form.id || uid() });
    setEditing(false); setCreating(false);
    if (!form.id) setSelectedId(null);
  };
  const del = id => {
    if (!window.confirm('Supprimer cet établissement ?')) return;
    deleteEtablissement(id); setSelectedId(null); setEditing(false);
  };

  // LIST VIEW
  if (!selectedId && !creating) return (
    <div>
      <div className="page-header">
        <div><div className="page-title">� Structures / Établissements ({etablissements.length})</div></div>
        <button className="btn btn-primary" onClick={startCreate}>+ Créer une structure</button>
      </div>
      {etablissements.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">🏢</div><div className="empty-state-text">Aucun établissement.</div><button className="btn btn-primary mt-3" onClick={startCreate}>+ Créer</button></div>
      ) : [...etablissements].sort((a,b) => (a.nom||'').localeCompare(b.nom||'')).map(etab => {
        const s = stats.find(x => x.id === etab.id) || {};
        const barColor = s.taux >= 70 ? 'progress-green' : s.taux >= 40 ? 'progress-orange' : 'progress-red';
        return (
          <div key={etab.id} className="etab-card"
            style={{ cursor:'pointer' }}
            onClick={() => setExpandedEtabId(id => id === etab.id ? null : etab.id)}>
            <div className="etab-card-header">
              <div className="etab-icon">🏢</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:700, fontSize:15, color:'#111'}}>
                  {etab.nom}
                </div>
                {etab.delegue && <div className="etab-meta">🤝 Délégué syndical : <strong>{etab.delegue}</strong></div>}
                {etab.adresse && <div className="etab-meta">📍 {etab.adresse}</div>}
                {etab.telephone && <div className="etab-meta">📞 <a href={`tel:${etab.telephone}`} style={{color:'#555'}}>{etab.telephone}</a></div>}
                {etab.email && <div className="etab-meta">✉️ <a href={`mailto:${etab.email}`} style={{color:'#555'}}>{etab.email}</a></div>}
                <div className="etab-meta" style={{marginTop:4, color:'#888'}}>{s.actifs||0} adhérent{(s.actifs||0)>1?'s':''}</div>
              </div>
              <div style={{textAlign:'right', flexShrink:0, marginLeft:12}}>
                <div style={{fontSize:18, fontWeight:700, color:'#CC0000'}}>{formatMontant(s.collected||0)}</div>
                <div style={{fontSize:12, color:'#bbb'}}>/ {formatMontant(s.expected||0)}</div>
                <div style={{fontSize:16, fontWeight:700, color: s.taux>=70?'#2e7d32':s.taux>=40?'#ff9800':'#CC0000', marginTop:2}}>{s.taux||0}%</div>
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:4, marginLeft:10}}>
                <button className="btn-icon" title="Modifier" onClick={e => { e.stopPropagation(); startEdit(etab); setSelectedId(etab.id); }}>✏️</button>
                <button className="btn-icon" title="Voir fiche détail" onClick={e => { e.stopPropagation(); setSelectedId(etab.id); setEditing(false); setExpanded(true); }}>📋</button>
                <button className="btn-icon" title="Supprimer" style={{color:'#c62828'}} onClick={e => { e.stopPropagation(); del(etab.id); }}>🗑️</button>
              </div>
            </div>
            <div style={{display:'flex', gap:8, alignItems:'center', marginTop:4}}>
              <div style={{flex:1}}>
                <div className="progress" style={{height:4}}>
                  <div className={`progress-bar ${barColor}`} style={{width:`${s.taux||0}%`}}/>
                </div>
              </div>
              <span style={{fontSize:11, color:'#9ca3af'}}>{expandedEtabId===etab.id ? '▲' : '▼'}</span>
            </div>
            {/* Adhérents inline */}
            {expandedEtabId === etab.id && (() => {
              const members = [...adherents.filter(a => a.etablissementId === etab.id)]
                .sort((a,b) => (a.nom||'').localeCompare(b.nom||''));
              return (
                <div style={{ borderTop:'1px solid #f0f0f0', marginTop:8, paddingTop:8 }} onClick={e => e.stopPropagation()}>
                  {members.length === 0 ? (
                    <div style={{fontSize:12,color:'#9ca3af',padding:'8px 0'}}>Aucun adhérent dans cette structure</div>
                  ) : members.map(adh => (
                    <div key={adh.id}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'6px 4px',borderRadius:6,cursor:'pointer',transition:'background .15s'}}
                      onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}
                      onClick={() => goToAdherent && goToAdherent(adh.id)}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:'#fee2e2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'#CC0000',flexShrink:0}}>
                        {(adh.prenom||' ')[0]}{(adh.nom||' ')[0]}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#111'}}>{adh.prenom} {adh.nom}</div>
                        <div style={{fontSize:10,color:'#888'}}>{adh.metier||'—'} · {adh.modePaiement}</div>
                      </div>
                      <span style={{fontSize:10,padding:'2px 7px',borderRadius:999,fontWeight:700,
                        background:adh.statut==='Actif'?'#dcfce7':adh.statut==='Suspendu'?'#fef3c7':'#fee2e2',
                        color:adh.statut==='Actif'?'#166534':adh.statut==='Suspendu'?'#92400e':'#991b1b'}}>
                        {adh.statut}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );

  const etab = creating ? form : (selected || form);
  const s = stats.find(x => x.id === etab.id) || {};
  const members = adherents.filter(a => a.etablissementId === etab.id);

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost" onClick={() => { setSelectedId(null); setEditing(false); setCreating(false); }}>← Retour</button>
          <div><div className="page-title">{creating ? 'Nouvel établissement' : etab.nom}</div></div>
        </div>
        <div className="flex gap-2">
          {!editing && !creating && <>
            <button className="btn btn-outline btn-sm" onClick={() => startEdit(etab)}>✏️ Modifier</button>
            <button className="btn btn-outline btn-sm" style={{color:'#c62828',borderColor:'#ef9a9a'}} onClick={() => del(etab.id)}>🗑️</button>
          </>}
          {editing && <>
            <button className="btn btn-outline" onClick={cancel}>Annuler</button>
            <button className="btn btn-primary" onClick={save} disabled={!form.nom}>💾 Enregistrer</button>
          </>}
        </div>
      </div>

      <div className="card mb-4">
        {editing ? (
          <div className="grid-2">
            {[['nom','Nom *'],['delegue','Délégué syndical FO'],['adresse','Adresse'],['telephone','Téléphone'],['email','Email']].map(([k,l]) => (
              <div key={k} className="form-group" style={k==='adresse'?{gridColumn:'1/-1'}:{}}>
                <label className="form-label">{l}</label>
                <input className="form-control" value={form[k]} onChange={e => f(k)(e.target.value)} />
              </div>
            ))}
            <div className="form-group" style={{gridColumn:'1/-1'}}>
              <label className="form-label">Notes</label>
              <textarea className="form-control" value={form.notes} onChange={e => f('notes')(e.target.value)} rows={3}/>
            </div>
          </div>
        ) : (
          <div className="grid-2">
            {[['Délégué syndical FO', etab.delegue],['Adresse', etab.adresse],['Téléphone', etab.telephone?<a href={`tel:${etab.telephone}`} style={{color:'#CC0000'}}>{etab.telephone}</a>:null],['Email', etab.email?<a href={`mailto:${etab.email}`} style={{color:'#CC0000'}}>{etab.email}</a>:null],['Notes', etab.notes]].map(([label,val],i) => (
              <div key={i} style={label==='Adresse'||label==='Notes'?{gridColumn:'1/-1'}:{}} className="mb-3">
                <div className="form-label">{label}</div>
                <div style={{fontSize:14,color:'#222',padding:'4px 0'}}>{val || <span style={{color:'#bbb'}}>—</span>}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!creating && (
        <div className="grid-4 mb-4">
          <div className="stat-card"><div className="stat-label">Adhérents actifs</div><div className="stat-value text-red">{s.actifs||0}</div><div className="stat-sub">sur {s.total||0} total</div></div>
          <div className="stat-card"><div className="stat-label">Collecté {selectedYear}</div><div className="stat-value">{formatMontant(s.collected||0)}</div></div>
          <div className="stat-card"><div className="stat-label">Attendu {selectedYear}</div><div className="stat-value">{formatMontant(s.expected||0)}</div></div>
          <div className="stat-card">
            <div className="stat-label">Taux</div><div className="stat-value">{s.taux||0}%</div>
            <div className="progress mt-2"><div className="progress-bar green" style={{width:`${s.taux||0}%`}}/></div>
          </div>
        </div>
      )}

      {!creating && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Adhérents ({members.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(x=>!x)}>{expanded?'▲ Réduire':'▼ Afficher'}</button>
          </div>
          {expanded && (
            members.length === 0 ? <div className="text-sm text-gray">Aucun adhérent dans cette structure.</div> :
            members.map(adh => (
              <div key={adh.id} className="adh-item" onClick={() => goToAdherent && goToAdherent(adh.id)}>
                <div className="adh-avatar" style={{width:32,height:32,fontSize:12}}>{initials(adh.prenom,adh.nom)}</div>
                <div style={{flex:1}}>
                  <div className="adh-name" style={{fontSize:13}}>{adh.prenom} {adh.nom}</div>
                  <div className="adh-sub">{adh.metier||'—'} · {adh.modePaiement}</div>
                </div>
                <span className={`badge badge-${adh.statut==='Actif'?'actif':adh.statut==='Suspendu'?'suspendu':'radie'}`}>{adh.statut}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
